import { useState, useEffect } from 'react'
import { generatePath } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'

import { SectionHeader } from 'screens/Finder/components/FileTree/Types'
import { FilesView, FilesViewSchema, SortingDescriptor, SortingDirection } from 'api/clients/FilesViewsClient'
import { Property, DefaultType, PropertyType, PersonPropertyValue } from 'api/clients/PropertyClient'
import { AlmanacFile as File } from 'types'
import { useFiles } from 'hooks/queries/finder/useFiles'
import { useRouteSchemaId } from 'hooks/useRouteSchemaId'
import { upsertProperties } from 'redux/actions/properties'
import { useLoadAssignedProperties } from 'hooks/useLoadAssignedProperties'
import { RootState } from 'types'
import { handbookLink } from 'components/Handbook2_0/util'
import { useHandbook } from 'components/Handbook2_0/hooks'
import { getArticlePath } from 'utils/urlUtils'
import { ALL_FILES_PATH } from 'paths'
import { useRootStore } from 'stores/RootStore'
import { FilterType } from 'screens/RecentDocsContainer/Types'
import { useQueryClient } from 'react-query'

interface Props {
  location: string
  workspaceId: number
  folderKey?: string
  numItems?: number
  type?: FilterType
  skipProjects?: boolean
  fileTypes?: string
}

export enum Bucket {
  Today = 'today',
  Yesterday = 'yesterday',
  ThisWeek = 'thisWeek',
  LastWeek = 'lastWeek',
  ThisMonth = 'thisMonth',
  LastMonth = 'lastMonth',
  Older = 'older',
}

const sortPropertyColumn = (sortProperty: Property) => {
  if (sortProperty.default_type === DefaultType.CreatedByMe) return 'created_by_me'
  if (sortProperty.default_type === DefaultType.Shared) return 'shared_at'
  if (sortProperty.default_type === DefaultType.Updated) return 'updated_at'
  if (sortProperty.default_type === DefaultType.LastUpdatedByMe) return 'last_updated'

  return 'last_updated'
}

const fileWithBucket = (file: File, sortProperty: Property) => {
  const date = file[sortPropertyColumn(sortProperty)]
  const createdAt = dayjs(date)
  const today = dayjs()

  if (createdAt.isSame(today, 'day')) {
    return { ...file, lastViewedBucket: Bucket.Today }
  }

  if (createdAt.isSame(today.subtract(1, 'day'), 'day')) {
    return { ...file, lastViewedBucket: Bucket.Yesterday }
  }

  if (createdAt.isSame(today, 'week')) {
    return { ...file, lastViewedBucket: Bucket.ThisWeek }
  }

  if (createdAt.isSame(today.subtract(1, 'week'), 'week')) {
    return { ...file, lastViewedBucket: Bucket.LastWeek }
  }

  if (createdAt.isSame(today, 'month')) {
    return { ...file, lastViewedBucket: Bucket.ThisMonth }
  }

  if (createdAt.isSame(today.subtract(1, 'month'), 'month')) {
    return { ...file, lastViewedBucket: Bucket.LastMonth }
  }

  return { ...file, lastViewedBucket: Bucket.Older }
}

const getFilesWithBucket = (files: File[], sortProperty: Property) =>
  files.map((file) => fileWithBucket(file, sortProperty))

const groupFilesWithSection = (filesView?: { files: File[]; schema: FilesViewSchema }) => {
  if (filesView?.schema.sorting_property?.type !== PropertyType.Date) return filesView?.files || []

  const headers = sectionHeaders(
    filesView?.files || [],
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    filesView?.schema.sorting_direction!,
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    filesView?.schema.sorting_property!,
  )

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const filesWithBuckets = getFilesWithBucket(filesView?.files || [], filesView?.schema.sorting_property!)

  if (filesView?.schema.sorting_direction === SortingDirection.Ascending) headers.reverse()

  return headers
    .map((header) => [
      {
        ...header,
        nodeType: 'sectionHeader',
      },
      ...filesWithBuckets.filter((fileWithBucket) => fileWithBucket.lastViewedBucket === header.id),
    ])
    .flat()
}

const sectionHeaders = (files: File[], sortDirection: SortingDirection, sortProperty: Property) => {
  if (sortProperty?.type !== PropertyType.Date) return []

  const headers: SectionHeader[] = []
  const filesWithBucket = files.map((file) => fileWithBucket(file, sortProperty))

  const headersIndices = {
    today: filesWithBucket.findIndex(({ lastViewedBucket }) => lastViewedBucket === Bucket.Today),
    yesterday: filesWithBucket.findIndex(({ lastViewedBucket }) => lastViewedBucket === Bucket.Yesterday),
    thisWeek: filesWithBucket.findIndex(({ lastViewedBucket }) => lastViewedBucket === Bucket.ThisWeek),
    lastWeek: filesWithBucket.findIndex(({ lastViewedBucket }) => lastViewedBucket === Bucket.LastWeek),
    thisMonth: filesWithBucket.findIndex(({ lastViewedBucket }) => lastViewedBucket === Bucket.ThisMonth),
    lastMonth: filesWithBucket.findIndex(({ lastViewedBucket }) => lastViewedBucket === Bucket.LastMonth),
    older: filesWithBucket.findIndex(({ lastViewedBucket }) => lastViewedBucket === Bucket.Older),
  }
  if (headersIndices.today >= 0) {
    headers.push({ id: Bucket.Today, title: 'Today', index: headersIndices.today })
  }
  if (headersIndices.yesterday >= 0) {
    headers.push({ id: Bucket.Yesterday, title: 'Yesterday', index: headersIndices.yesterday })
  }
  if (headersIndices.thisWeek >= 0) {
    headers.push({ id: Bucket.ThisWeek, title: 'This week', index: headersIndices.thisWeek })
  }
  if (headersIndices.lastWeek >= 0) {
    headers.push({ id: Bucket.LastWeek, title: 'Last week', index: headersIndices.lastWeek })
  }
  if (headersIndices.thisMonth >= 0) {
    headers.push({ id: Bucket.ThisMonth, title: 'This month', index: headersIndices.thisMonth })
  }
  if (headersIndices.lastMonth >= 0) {
    headers.push({ id: Bucket.LastMonth, title: 'Last month', index: headersIndices.lastMonth })
  }
  if (headersIndices.older >= 0) {
    if (sortDirection === SortingDirection.Ascending) {
      headers.push({ id: Bucket.Older, title: 'Older than last month', index: headersIndices.older })
    } else {
      headers.push({ id: Bucket.Older, title: 'Older', index: headersIndices.older })
    }
  }
  return headers
}

export const useFilesTable = ({
  location,
  workspaceId,
  folderKey,
  numItems = 15,
  type,
  skipProjects = false,
  fileTypes,
}: Props) => {
  const [filesView, setFilesView] = useState<{ files: File[]; schema: FilesViewSchema }>()
  const [files, setFiles] = useState<File[]>([])
  const { loadAssignedProperties, fileToAssignee } = useLoadAssignedProperties()
  const assignedProperties = useSelector(({ filesProperties }: RootState) => filesProperties)
  const currentHandbook = useHandbook()
  const schemaId = useRouteSchemaId()
  const queryClient = useQueryClient()
  const dispatch = useDispatch()
  const { workspaceTemplatesStore: templates } = useRootStore()

  const [sortParam, setSortParam] = useState<SortingDescriptor>({
    sorting_property_id: null,
    sorting_direction: SortingDirection.Descending,
  })

  useEffect(() => {
    setFiles((location === 'handbook_folder' ? filesView?.files || [] : groupFilesWithSection(filesView)) as File[])
  }, [filesView, location])

  const getAssignedProperty = (file: File, property: Property) => {
    return assignedProperties[file.id]?.[property.id]
  }

  const fileLink = (file: File) => {
    if (file.type === 'article') {
      if (currentHandbook) return handbookLink(currentHandbook, file.slug, true)

      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return getArticlePath({ slug: file.slug! })
    }
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (file.handbook) return handbookLink(file.handbook!, undefined, true)

    if (file.type === 'media_file' && file.url) {
      return file.url
    }

    if (file.type === 'design' && file.url) {
      return file.url
    }

    return generatePath(ALL_FILES_PATH, { workspaceId: file.real_workspace_id, location: file.id })
  }

  const getPersonPropertyValue = (file: File, property: Property) => {
    if (property.type !== PropertyType.Person) return ''

    const assignedProperty = getAssignedProperty(file, property)

    if (!assignedProperty || !assignedProperty.values) return ''

    return assignedProperty.values[0] as PersonPropertyValue
  }

  const query = {
    items: numItems,
    current_schema_id: schemaId,
    sorting_property_id: sortParam.sorting_property_id,
    sorting_direction: sortParam.sorting_direction,
    accessible_as_template: !!templates?.selectedFile,
    skip_projects: skipProjects,
    file_types: fileTypes,
    type,
  }
  const filesResult = useFiles({
    location,
    workspaceId,
    files_view_project_id: folderKey,
    query,
  })

  useEffect(() => {
    if (!filesResult.isSuccess) return

    const lastPageIndex = filesResult.data.pages.length - 1
    const lastPage = filesResult.data.pages[lastPageIndex]
    const { schema } = lastPage.data
    if (!schema) return
    const { properties } = schema
    const propertiesIds = schema.properties.map(({ id }: { id: number }) => id)
    const files = filesResult.data.pages.map((page) => page.data.files).flat()
    lastPageIndex === 0 && dispatch(upsertProperties(properties))
    loadAssignedProperties(files, propertiesIds)

    setFilesView({ schema, files })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesResult.data])

  const isLast = (index: number) => files.length - 1 === index

  const handleSort = (property: Property) => {
    const sortProperty = filesView?.schema.sorting_property_id
    let sortDierction = filesView?.schema.sorting_direction
    let finalDirection = SortingDirection.Ascending

    if (filesView && sortProperty !== property.id && sortDierction) {
      finalDirection = sortDierction
    } else {
      finalDirection =
        sortDierction === SortingDirection.Ascending ? SortingDirection.Descending : SortingDirection.Ascending
    }

    setSortParam({
      sorting_direction: finalDirection,
      sorting_property_id: property.id,
    })
  }

  const removeFile = async (fileId: string) => {
    setFilesView((prevFilesView) => {
      if (prevFilesView)
        return {
          ...prevFilesView,
          files: prevFilesView?.files.filter((file) => file.id !== fileId),
        }
    })

    // Update query data with new values so that loading a new page doesn't bring back the deleted file
    const queryKey = ['workspace', workspaceId, 'files', location, JSON.stringify(query), folderKey]
    const currentData = queryClient.getQueryData<{ pages: { data: FilesView<File> }[] }>(queryKey)
    const newData = currentData
      ? {
          ...currentData,
          pages: currentData.pages.map((page) => {
            if (page.data.files.find((f: File) => f.id === fileId)) {
              return {
                ...page,
                data: {
                  ...page.data,
                  files: page.data.files.filter((f: File) => f.id !== fileId),
                },
              }
            }

            return page
          }),
        }
      : {}
    queryClient.setQueryData(queryKey, newData)
  }

  return {
    handleSort,
    isLast,
    files,
    fileLink,
    properties: filesView?.schema?.properties,
    sorting_property_id: filesView?.schema.sorting_property_id,
    sorting_direction: filesView?.schema.sorting_direction,
    hasNextPage: filesResult.hasNextPage,
    fetchNextPage: filesResult.fetchNextPage,
    isLoading: filesResult.isLoading,
    isFetchingNextPage: filesResult.isFetchingNextPage,
    isFetching: filesResult.isFetching,
    fileToAssignee,
    getAssignedProperty,
    getPersonPropertyValue,
    removeFile,
  }
}
